import React, { useState } from 'react';
import { X, AlertTriangle, ArrowLeft, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Modal para devolver un pedido a Facturación.
 * Permite a Cartera/Admin devolver órdenes en estado "revision_cartera" 
 * de vuelta a "pendiente_por_facturacion" para corrección.
 */
const ReturnToBillingModal = ({ isOpen, onClose, order, onSuccess }) => {
    const [reason, setReason] = useState('');
    const [cleanFlags, setCleanFlags] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // No renderizar si no está abierto
    if (!isOpen || !order) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validación básica del motivo
        if (!reason || reason.trim().length < 3) {
            toast.error('El motivo debe tener al menos 3 caracteres');
            return;
        }

        setIsSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/cartera/orders/${order.id}/return-to-billing`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    reason: reason.trim(),
                    cleanFlags,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al devolver el pedido');
            }

            toast.success('Pedido devuelto a Facturación correctamente');

            // Limpiar formulario
            setReason('');
            setCleanFlags(false);

            // Notificar éxito al componente padre
            if (onSuccess) {
                onSuccess(data);
            }

            onClose();
        } catch (error) {
            console.error('Error al devolver pedido:', error);
            toast.error(error.message || 'Error al devolver el pedido a Facturación');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setReason('');
            setCleanFlags(false);
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                            <ArrowLeft className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">
                                Devolver a Facturación
                            </h2>
                            <p className="text-sm text-gray-500">
                                Pedido #{order.order_number || order.id}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Warning Alert */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <p className="font-medium text-orange-900 mb-1">
                                Esta acción devolverá el pedido a Facturación
                            </p>
                            <p className="text-orange-700">
                                El pedido cambiará de estado <strong>revision_cartera</strong> a{' '}
                                <strong>pendiente_por_facturacion</strong> para que pueda ser corregido.
                                Esta acción quedará registrada en el historial de auditoría.
                            </p>
                        </div>
                    </div>

                    {/* Order Info */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex items-start gap-3">
                            <FileText className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-sm font-medium text-gray-700">Cliente:</span>
                                    <span className="text-sm text-gray-900">{order.customer_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm font-medium text-gray-700">Pedido/Factura:</span>
                                    <span className="text-sm text-gray-900">
                                        {order.siigo_invoice_number || order.invoice_code || order.order_number}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm font-medium text-gray-700">Estado actual:</span>
                                    <span className="text-sm font-medium text-orange-600">
                                        {order.status || 'revision_cartera'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm font-medium text-gray-700">Total:</span>
                                    <span className="text-sm text-gray-900">
                                        ${Number(order.total_amount || 0).toLocaleString('es-CO')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Reason Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Motivo de la devolución <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Ej: Error en método de pago, corrección de dirección de envío, etc."
                            rows={4}
                            disabled={isSubmitting}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                            required
                            minLength={3}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Mínimo 3 caracteres. Este motivo quedará registrado en el historial de auditoría.
                        </p>
                    </div>

                    {/* Advanced Options */}
                    <div className="border-t pt-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={cleanFlags}
                                onChange={(e) => setCleanFlags(e.target.checked)}
                                disabled={isSubmitting}
                                className="mt-1 w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:cursor-not-allowed"
                            />
                            <div className="flex-1">
                                <span className="text-sm font-medium text-gray-700">
                                    Limpiar información de pago
                                </span>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Opcional: Eliminar campos como requires_payment, paid_amount y siigo_payment_info.
                                    Útil si el pedido necesita re-evaluación completa de pago.
                                </p>
                            </div>
                        </label>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !reason || reason.trim().length < 3}
                            className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                            {isSubmitting ? 'Devolviendo...' : 'Confirmar Devolución'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReturnToBillingModal;
